import React, { useMemo, useState, useCallback, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import styles from './AnalyticsLineChart.module.css';
import { AnalyticsLineChartProps, ChartSeries, SeriesPoint } from './types';
import { aggregate, countDays, normalizeDate } from './utils/date';
import { formatValue } from './utils/format';

function resolveFormatter(series: ChartSeries) {
  return (value: number, currency?: string) => {
    if (typeof series.valueFormatter === 'function') {
      return series.valueFormatter(value, currency);
    }
    return formatValue(value, series.valueFormatter, currency);
  };
}

// Custom tooltip component for absolute mode
const AbsoluteTooltip = (props: any) => {
  const { active, payload, seriesMeta } = props;

  if (!active || !payload || !payload.length) {
    return null;
  }

  const dataPoint = payload[0]?.payload;
  const dateValue = dataPoint?.originalDate || dataPoint?.dateObj;
  const currentYear = new Date().getFullYear();
  
  const dateLabel = dateValue instanceof Date
    ? (dateValue.getFullYear() === currentYear
      ? dateValue.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      : dateValue.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }))
    : '';

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '8px 12px',
        color: '#333',
        fontSize: 12,
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      {dateLabel && <div style={{ marginBottom: 4, fontWeight: 600 }}>{dateLabel}</div>}
      {payload.map((entry: any, index: number) => {
        const meta = seriesMeta?.[entry.dataKey];
        const formatted = meta?.fmt ? meta.fmt(entry.value, dataPoint?.currency) : entry.value;
        return (
          <div key={index}>
            {meta?.name || entry.name}: <strong>{formatted}</strong>
          </div>
        );
      })}
    </div>
  );
};

// Custom tooltip component for relative/comparison mode
const RelativeTooltip = (props: any) => {
  const { active, payload, customLabelFormatter, seriesMeta } = props;

  if (!active || !payload || !payload.length) {
    return null;
  }

  const dataPoint = payload[0]?.payload;
  const originalDayOffset = dataPoint?.originalDayOffset;
  const dateObj = dataPoint?.dateObj;

  // Generate heading using custom formatter if available
  // Use originalDayOffset (negative values like -30, -3, 0) for the label
  let heading = '';
  if (customLabelFormatter && originalDayOffset !== undefined) {
    const effective = Math.min(originalDayOffset, 0);
    heading = customLabelFormatter(dateObj, effective);
  }

  // Sort entries by year (newest first)
  const entries = payload
    .map((entry: any) => {
      const meta = seriesMeta?.[entry.dataKey];
      const formatted = meta?.fmt ? meta.fmt(entry.value, dataPoint?.currency) : entry.value;
      const year = meta?.year ?? (dataPoint?.originalDate instanceof Date ? dataPoint.originalDate.getFullYear() : undefined);
      return { year, valueText: formatted, color: entry.stroke, name: meta?.name };
    })
    .sort((a: any, b: any) => {
      const ay = a.year ?? -Infinity;
      const by = b.year ?? -Infinity;
      return by - ay;
    });

  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        padding: '8px 12px',
        color: '#333',
        fontSize: 12,
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}
    >
      {heading && <div style={{ fontWeight: 600, marginBottom: 4 }}>{heading}</div>}
      {entries.map((e: any, index: number) => (
        <div key={index}>
          {e.year !== undefined ? e.year : '—'} — <strong>{e.valueText}</strong>
        </div>
      ))}
    </div>
  );
};

export const AnalyticsLineChart: React.FC<AnalyticsLineChartProps> = ({
  series,
  isLoading = false,
  noDataMessage = 'No data available',
  legend = { show: true, truncateAfter: 20, maxItems: 5, tooltipOnTruncate: true },
  headerSummary = { show: false, mode: 'single' },
  time = { alignMode: 'absolute', granularity: 'daily', autoAggregate: false, aggregationThresholdDays: 150, axisType: 'date' },
  performance = { bullets: 'auto', maxSeries: 5 },
  ariaLabel,
  className,
  style,
  height = 360,
  yAxisLabel
}) => {
  const [latestBySeries, setLatestBySeries] = useState<Record<string, { value: number; currency?: string }>>({});

  // Prepare series with aggregation
  const preparedSeries = useMemo(() => {
    if (time.autoAggregate) {
      if (time.granularity && time.granularity !== 'daily') {
        return series.map((s) => ({
          ...s,
          data: aggregate(
            s.data,
            time.granularity!,
            s.aggregationStrategy === 'latest' ? 'latest' : 'sum'
          )
        }));
      }
      
      const thresholdDays = time.aggregationThresholdDays || 367;
      const maxDataPoints = Math.max(...series.map(s => s.data?.length || 0));
      
      if (maxDataPoints > thresholdDays) {
        const weeklyAggregated = series.map((s) => ({
          ...s,
          data: aggregate(s.data, 'weekly', s.aggregationStrategy === 'latest' ? 'latest' : 'sum')
        }));
        const maxWeeklyPoints = Math.max(...weeklyAggregated.map(s => s.data?.length || 0));
        
        if (maxWeeklyPoints > thresholdDays) {
          return series.map((s) => ({
            ...s,
            data: aggregate(s.data, 'monthly', s.aggregationStrategy === 'latest' ? 'latest' : 'sum')
          }));
        }
        
        return weeklyAggregated;
      }
    }
    
    return series;
  }, [series, time.autoAggregate, time.granularity, time.aggregationThresholdDays]);

  const allSeries = useMemo(() => preparedSeries.slice(0, performance.maxSeries || 5), [preparedSeries, performance.maxSeries]);
  const hasData = allSeries.some((s) => s.data && s.data.length);

  // Build unified chart data and compute latest values
  const { chartData, seriesMeta } = useMemo(() => {
    const latest: Record<string, { value: number; currency?: string }> = {};
    const meta: Record<string, { name: string; fmt: (v: number, c?: string) => string; year?: number }> = {};
    
    // For each series, track formatter metadata
    allSeries.forEach((s) => {
      meta[`value_${s.id}`] = {
        name: s.name,
        fmt: resolveFormatter(s),
        year: s.year
      };
      if (s.data.length) {
        const last = s.data[s.data.length - 1];
        latest[s.id] = { value: last.value || 0, currency: last.currency };
      }
    });

    // Build unified data array
    const currentYear = new Date().getFullYear();
    const dataMap = new Map<number, any>();

    allSeries.forEach((s, idx) => {
      s.data.forEach((p, i) => {
        let timestamp: number;
        let dateObj: Date;
        let originalDate: Date | undefined;
        let dayOffset: number | undefined = p.dayOffset;

        if (time.alignMode === 'relativeStart') {
          const offset = p.dayOffset !== undefined ? p.dayOffset : i;
          dateObj = new Date(1970, 0, 1 + offset);
          timestamp = dateObj.getTime();
          originalDate = normalizeDate(p.date);
          dayOffset = offset;
        } else {
          dateObj = normalizeDate(p.date);
          timestamp = dateObj.getTime();
        }

        if (!dataMap.has(timestamp)) {
          dataMap.set(timestamp, {
            timestamp,
            dateObj,
            originalDate,
            dayOffset,
            originalDayOffset: (p as any).originalDayOffset,
            currency: p.currency
          });
        }
        
        const point = dataMap.get(timestamp);
        point[`value_${s.id}`] = p.value;
        // Store currency from first series with currency
        if (p.currency && !point.currency) {
          point.currency = p.currency;
        }
      });
    });

    // Sort by timestamp
    const data = Array.from(dataMap.values()).sort((a, b) => a.timestamp - b.timestamp);
    
    // Update latest state
    setLatestBySeries(latest);
    
    return { chartData: data, seriesMeta: meta };
  }, [allSeries, time.alignMode]);

  // X-axis tick formatter
  const formatXAxisTick = useCallback((timestamp: number) => {
    const date = new Date(timestamp);
    
    if (time.alignMode === 'relativeStart' && time.customLabelFormatter) {
      // For relative mode, calculate the day offset from synthetic date
      const baseSynthetic = new Date(1970, 0, 1);
      const dayOffset = Math.round((timestamp - baseSynthetic.getTime()) / (1000 * 60 * 60 * 24));
      
      // Find minOriginalOffset across all data
      let minOriginalOffset = 0;
      allSeries.forEach((s) => {
        s.data.forEach((p: any) => {
          if (typeof p.originalDayOffset === 'number' && p.originalDayOffset < minOriginalOffset) {
            minOriginalOffset = p.originalDayOffset;
          }
        });
      });
      
      const originalOffset = dayOffset + minOriginalOffset;
      return time.customLabelFormatter(date, originalOffset);
    }
    
    const currentYear = new Date().getFullYear();
    if (date.getFullYear() === currentYear) {
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
  }, [time.alignMode, time.customLabelFormatter, allSeries]);

  // Y-axis tick formatter
  const formatYAxisTick = useCallback((value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  }, []);

  // Determine if bullets should be shown
  const showBullets = useMemo(() => {
    if (performance.bullets === 'always') return true;
    if (performance.bullets === 'never') return false;
    const maxLength = Math.max(...allSeries.map(s => countDays(s.data)));
    return allSeries.length <= 2 && maxLength <= 370;
  }, [performance.bullets, allSeries]);

  // Header summary
  const header = useMemo(() => {
    if (!headerSummary?.show) return null;
    const primaryId = headerSummary.primarySeriesId || (series[0]?.id ?? 's0');
    const primary = latestBySeries[primaryId];
    
    if (headerSummary.mode === 'single') {
      return (
        <div className={styles.headerSummary}>
          <div className={styles.headerItem}>
            <span className={styles.headerLabel}>{(series.find(s => s.id === primaryId)?.name) || 'Selected'}</span>
            <span className={styles.headerValuePrimary}>
              {primary ? formatValue(primary.value, typeof series[0]?.valueFormatter === 'string' ? series[0]?.valueFormatter : 'integer', primary.currency) : '—'}
            </span>
          </div>
        </div>
      );
    }
    
    if (headerSummary.mode === 'paired' && series.length >= 2) {
      const s0 = series[0];
      const s1 = series[1];
      const l0 = latestBySeries[s0.id];
      const l1 = latestBySeries[s1.id];
      return (
        <div className={styles.headerSummary}>
          <div className={styles.headerItem}>
            <span className={styles.headerLabel}>{s0.name}</span>
            <span className={styles.headerValuePrimary}>{l0 ? resolveFormatter(s0)(l0.value, l0.currency) : '—'}</span>
          </div>
          <div className={styles.headerItem}>
            <span className={styles.headerLabel}>{s1.name}</span>
            <span className={styles.headerValueSecondary}>{l1 ? resolveFormatter(s1)(l1.value, l1.currency) : '—'}</span>
          </div>
        </div>
      );
    }
    
    // multiCompact
    return (
      <div className={styles.headerSummary}>
        {series.slice(0, performance.maxSeries || 5).map((s, idx) => {
          const l = latestBySeries[s.id];
          return (
            <div className={styles.headerItem} key={s.id} title={s.name}>
              <span className={styles.headerLabel}>{s.name}</span>
              <span className={idx === 0 && !s.dashedMuted ? styles.headerValuePrimary : styles.headerValueSecondary}>
                {l ? resolveFormatter(s)(l.value, l.currency) : '—'}
              </span>
            </div>
          );
        })}
      </div>
    );
  }, [headerSummary, latestBySeries, performance.maxSeries, series]);

  // Legend view
  const legendView = useMemo(() => {
    if (!legend?.show) return null;
    const truncate = legend.truncateAfter ?? 20;
    const items = series.slice(0, legend.maxItems ?? 5);
    return (
      <div className={styles.legend}>
        {items.map((s) => {
          const label = s.name;
          const truncated = label.length > truncate ? `${label.slice(0, truncate)}…` : label;
          return (
            <div key={s.id} className={styles.legendItem} title={legend.tooltipOnTruncate && truncated !== label ? label : undefined}>
              <span 
                className={styles.legendSwatch} 
                style={{ 
                  background: s.color, 
                  border: s.dashedMuted ? '1px dashed ' + s.color : 'none' 
                }} 
              />
              <span className={styles.legendLabel}>{truncated}</span>
            </div>
          );
        })}
      </div>
    );
  }, [legend, series]);

  // Custom tooltip based on mode
  const tooltipContent = useMemo(() => {
    if (time.alignMode === 'relativeStart') {
      return (
        <RelativeTooltip 
          customLabelFormatter={time.customLabelFormatter} 
          seriesMeta={seriesMeta} 
        />
      );
    }
    return <AbsoluteTooltip seriesMeta={seriesMeta} />;
  }, [time.alignMode, time.customLabelFormatter, seriesMeta]);

  return (
    <div className={`${styles.grzChart} ${className || ''}`} style={{ ...style }} aria-label={ariaLabel}>
      {header}
      <div className={styles.chartArea} style={{ height }}>
        {hasData ? (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 16, right: 16, left: 0, bottom: 8 }}
            >
              <CartesianGrid strokeOpacity={0.1} vertical={false} />

              <XAxis
                dataKey="timestamp"
                type="number"
                scale="time"
                domain={['dataMin', 'dataMax']}
                tickFormatter={formatXAxisTick}
                tick={{ fontSize: 11, fill: '#666' }}
                axisLine={{ stroke: '#e0e0e0' }}
                tickLine={false}
                minTickGap={60}
              />

              <YAxis
                tickFormatter={formatYAxisTick}
                tick={{ fontSize: 11, fill: '#666' }}
                axisLine={false}
                tickLine={false}
                width={yAxisLabel ? 70 : 50}
                domain={[0, 'auto']}
                label={yAxisLabel ? {
                  value: yAxisLabel,
                  angle: -90,
                  dx: 0,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: 12, fill: '#666' }
                } : undefined}
              />

              <Tooltip
                content={React.cloneElement(tooltipContent as React.ReactElement, { seriesMeta })}
                cursor={{ stroke: '#ccc', strokeWidth: 1 }}
              />

              {/* Render a line for each series */}
              {allSeries.map((s, idx) => (
                <Line
                  key={s.id}
                  type="monotone"
                  dataKey={`value_${s.id}`}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={idx === 0 && !s.dashedMuted ? 3 : 2}
                  strokeDasharray={s.dashedMuted ? '3 3' : undefined}
                  fill={s.color}
                  fillOpacity={idx === 0 && !s.dashedMuted ? 0.1 : 0.05}
                  dot={false}
                  activeDot={false}
                  animationDuration={1000}
                  animationEasing="ease-out"
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : null}
      </div>
      {legendView}
      {isLoading && (
        <div className={styles.loadingOverlay}>Loading…</div>
      )}
      {!isLoading && !hasData && (
        <div style={{ padding: 12, color: 'var(--text-secondary)' }}>{noDataMessage}</div>
      )}
    </div>
  );
};

export default AnalyticsLineChart;
